import { useEffect } from 'react';

/**
 * Hiệu ứng xuất hiện khi cuộn tới (scroll reveal) bằng IntersectionObserver.
 *
 * Cách dùng: gắn thuộc tính `data-reveal` cho phần tử cần animate
 * (tuỳ chọn `data-reveal="up | left | right | zoom"`, mặc định là "up").
 * Có thể thêm độ trễ so le bằng inline style: style={{ '--reveal-delay': '120ms' }}.
 *
 * Hook chỉ cần gọi một lần trong trang. Tôn trọng prefers-reduced-motion.
 *
 * @param {React.DependencyList} [deps] - dependencies để quét lại DOM khi nội dung đổi.
 */
export default function useScrollReveal(deps = []) {
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const reveal = (el) => el.classList.add('is-visible');

        // Hiện ngay mọi phần tử data-reveal đang nằm trong (hoặc đã đi qua) viewport.
        // Tránh trường hợp nhảy thẳng tới một section qua liên kết neo (#faq, ...)
        // khiến IntersectionObserver không kịp bắt sự kiện và phần tử kẹt ở opacity 0.
        const revealInView = () => {
            document.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((el) => {
                const rect = el.getBoundingClientRect();
                if (rect.top < window.innerHeight && rect.bottom > 0) reveal(el);
            });
        };

        const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (prefersReduced || !('IntersectionObserver' in window)) {
            document.querySelectorAll('[data-reveal]').forEach(reveal);
            return;
        }

        const observer = new IntersectionObserver(
            (entries, obs) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        reveal(entry.target);
                        obs.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
        );

        const els = Array.from(document.querySelectorAll('[data-reveal]:not(.is-visible)'));
        els.forEach((el) => observer.observe(el));

        // Lưới an toàn: hiện những phần tử đã ở trong viewport ngay khi
        // load, sau khi trang tải xong, và một lần nữa sau khoảng trễ ngắn.
        revealInView();
        window.addEventListener('load', revealInView);
        const fallback = window.setTimeout(revealInView, 600);

        return () => {
            observer.disconnect();
            window.removeEventListener('load', revealInView);
            window.clearTimeout(fallback);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
